import React, { useEffect } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { useClientItems } from '@/hooks/item';
import { useCurrentEmailItem, useCurrentLoginIdItem, useCurrentPhoneNumberItem } from '@/hooks/reference';
import { ClientTab } from '@/types/common';
import { Client } from '@/types/client';
import { RQQuery } from '@/types/query';
import { ServiceErrorView } from '@/components/atoms/Error';
import { ShowMore, Heading } from '@/components/atoms/Sidebar';
import Item from '@/components/molecules/sidebar/Item';
import ResultView from '@/components/molecules/sidebar/Result';
import Tabs from '@/components/molecules/common/Tabs';
import styles from './Common.module.scss';

export const ClientView: React.FC<{
  isEmailTab: boolean;
  isPhoneTab: boolean;
  inputValue: string;
  clientTab: ClientTab;
  clientTabs: ClientTab[];
  setClientTab: (t: ClientTab) => void;
  setCurrentItem: (t: string) => void;
  currentItem: string;
  data: RQQuery<Client[]>;
}> = ({ isEmailTab, isPhoneTab, inputValue, clientTab, setClientTab, setCurrentItem, currentItem, data }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (currentItem) setCurrentItem('');
  }, [inputValue]);

  const tabLabels = [
    t('sidebar.tabs.bought'),
    t('sidebar.tabs.sold'),
    t('sidebar.tabs.listed'),
    t('sidebar.tabs.others'),
  ];

  const onChangeTab = (_: React.SyntheticEvent, newTab: number) => {
    setClientTab(newTab);
  };

  return (
    <div
      className={clsx([styles.container, styles.containerClient])}
      data-cy="SideBar-Client"
    >
      <div className={styles.horizontalPadding}>
        <Heading label={`${t('sidebar.result_for')} '${inputValue}'`} />
      </div>
      {data.kind === 'Called' && (
        <div className={styles.horizontalPadding}>
          <Skeleton
            height={100}
            variant="rounded"
          />
        </div>
      )}
      {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
      {data.kind === 'Loaded' && data.data.length > 0 && (
        <>
          <div className={styles.allPadding}>
            <ResultView
              onClick={() => setCurrentItem('')}
              head={data.data[0].name}
              sub={data.data[0].email}
              active={currentItem === ''}
            />
            <div />
            <Heading label={`${t('sidebar.item_belong_to')} '${inputValue}'`} />
            <div
              className={styles.tabs}
              data-cy="SideBar-ClientTabs"
            >
              <Tabs
                onChangeTab={onChangeTab}
                labels={tabLabels}
                current={clientTab}
                sx={{ '& .MuiTab-root': { minWidth: 'auto', padding: '0 1rem' } }}
              />
            </div>
          </div>
          <div
            className={styles.tabContent}
            data-cy="SideBar-ClientTab__cards"
          >
            <div
              className={styles.tabWrapper}
              data-cy={`SideBar-ClientTab__${clientTab}`}
            >
              <ClientItemsView
                isEmailTab={isEmailTab}
                isPhoneTab={isPhoneTab}
                id={Number(data.data[0].clientId)}
                tab={clientTab}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ClientItemsView: React.FC<{
  isEmailTab: boolean;
  isPhoneTab: boolean;
  id: number;
  tab: ClientTab;
}> = ({ isEmailTab, isPhoneTab, id, tab }) => {
  const { t } = useTranslation();

  const path = () => {
    switch (tab) {
      case ClientTab.BOUGHT:
        return 'bought-items';
      case ClientTab.SOLD:
        return 'sold-items';
      case ClientTab.LISTED:
        return 'listed-items';
      case ClientTab.OTHERS:
        return 'other-items';
    }
  };

  const [currentItemEmail, setCurrentItemEmail] = useCurrentEmailItem();
  const [currentItemId, setCurrentItemId] = useCurrentLoginIdItem();
  const [currentPhoneNumber, setCurrentPhoneNumber] = useCurrentPhoneNumberItem();
  const { data, isLoading, isLoadingMore, isEmpty, fetchNextPage } = useClientItems(id, path(), 10);
  const items = data.kind === 'Ok' && data.data ? data.data.products : [];

  const setCurrentItem = isEmailTab ? setCurrentItemEmail : isPhoneTab ? setCurrentPhoneNumber : setCurrentItemId;
  const currentItem = isEmailTab ? currentItemEmail : isPhoneTab ? currentPhoneNumber : currentItemId;

  return (
    <div className={styles.itemContainer}>
      {isLoading && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {!isLoading && data.kind === 'Ko' && <ServiceErrorView error={data.error} />}
      {!isLoading && isEmpty && t('common.no_item_found')}
      {!isLoading && data.kind === 'Ok' && (
        <>
          {items.map((item) => (
            <Item
              onClick={() => setCurrentItem(String(item.productId))}
              active={String(item.productId) === currentItem}
              key={item.productId}
              productId={item.productId}
              productName={item.productName}
              brandName={item.brandName}
              soldDate={item.soldDate}
              imageUrl={item.imageUrl}
            />
          ))}
          <div className={styles.showMore}>
            {data.kind === 'Ok' && data.data.hasMore && (
              <ShowMore
                disabled={isLoading || isLoadingMore}
                onClick={() => {
                  if (data.kind === 'Ok' && data.data.hasMore) {
                    fetchNextPage();
                  }
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
